<script>
export default {
	name: 'AnchorNavigation',
	inheritAttrs: false,
};
</script>

<script setup>
import Parsys from '~/components/Parsys.vue';
import { getCompositeComponent } from '@custom/aem-vue-editable-components';
import VueSwiper from '~/components/VueSwiper.vue';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { gsap } from 'gsap';
import Dropdown from '~/components/form/Dropdown.vue';
import { scrollToElement } from '~/logic/helpers/dom.js';
import { isMobileViewport } from '~/logic/composables/breakpoints.js';
import { useRouteHash } from '~/logic/composables/useRouteHash.js';
import { useWatchPausable } from '~/logic/composables/useWatchPausable.js';
import { debounce as _debounce } from 'lodash';


gsap.registerPlugin(ScrollTrigger);

const props = defineProps({
	anchorNavigationMultilistPojo: { type: Array, default: () => [] },
	mobileTextLabel: { type: String, default: '' },
	isHideInDesktop: { type: Boolean, default: false },
	isHideInMobile: { type: Boolean, default: false },
	componentIdAA: { type: String, default: '' },
});

const { isEditorMode } = useGlobalAEMState();


const createParsys = computed(() => {
	return Array(props.anchorNavigationMultilistPojo.length).fill().map((item, index) => {
		return {
			component: getCompositeComponent({
				type: 'mh/components/mh-layout-container',
				componentKey: `anchor-navigation-parsys-${index}`,
			}),
		};
	});
});

const tabList = ref([]);
const swiperEl = ref(null);
const swiperBtnPrevEl = ref(null);
const swiperBtnNextEl = ref(null);
const activeSectionId = ref(null);
const progressBarPercentage = ref(0);
const isSlidesLongerThanScreen = ref(false);
const selectedMobileTab = ref('');

let scrollTriggerStickyInstance = null;
let scrollTriggerContentInstances = [];


const createTabs = () => {
	return props.anchorNavigationMultilistPojo
		.map(({ label, value }, index) => {
			return {
				id: index,
				label,
				...{ value: value.replace(/\s+/g, '-').toLowerCase() },
			};
		});
};

const initStickyTab = () => {
	// get the element to observe
	const parentElement = document.querySelector('.AnchorNavigation');
	const childElement = parentElement.querySelector('.AnchorNavigation-bar-container');
	const stickyElement = parentElement.querySelector('.AnchorNavigation-bar');

	// create a new intersection observer
	const parentObserver = new IntersectionObserver((entries) => {
		const targetEntry = entries[0];
		if (targetEntry.isIntersecting && targetEntry.boundingClientRect.bottom < 100) {
			// scroll up and back to the bottom of component
			stickyElement.classList.add('sticked');
		} else {
			// scroll down and left the bottom of component
			stickyElement.classList.remove('sticked');
		}
	});

	const childObserver = new IntersectionObserver( (entries) => {
		const targetEntry = entries[0];
		const isScrollUp = document.querySelector('html').classList.contains('scroll-dir-up');
		
		if (targetEntry.boundingClientRect.top <= 0 && !isScrollUp) {
			// scroll down and enter the top of component
			stickyElement.classList.add('sticked');
		}

		if ((targetEntry.boundingClientRect.top > -100) && isScrollUp) {
			// scroll up and left the top of component
			stickyElement.classList.remove('sticked');
		}
	});

	// observe the element
	parentObserver.observe(parentElement);
	childObserver.observe(childElement);
};

const init = async () => {
	if (document.querySelector('.AnchorNavigation').length > 1) throw new Error('There is more than 1 Anchor Navigation Component in the page.');

	initContentResizeOberver();
	initScrollListener();
	await nextTick();
	initStickyTab();
	// initContentScrollTriggers();
	updateStatusSlidesLongerThanScreen();

	if (window.location.hash) {
		const hashValue = window.location.hash.substring(1);
		const index = tabList.value.find((data) => data.value === hashValue)?.id;
		activateTab(index);
		handleSlideToSection(index);
	}
	
	isLoadOnce.value = true;
};

const subpageHashKeyWhitelist = [
	'flight-search',
	'flight-hotel-search',
	'flight-schedule',
	'flight-status',
	'inflight-menu',
	'check-in',
	'manage-booking',
	'mhe-flight-search',
];

// This method here to prevent hash overriden when hash outside of this component. Return truthy wether hash param match with whitelist subpage hash. For instance click CTA button to open subpage. 
const isValueSubpageInWhitelist = () => {
	const url = new URL(window.location);
	const isSubpageInWhitelist = subpageHashKeyWhitelist.includes(url.hash.replace('#', ''));
	
	
	return isSubpageInWhitelist;
};


const activateTab = (index) => {
	
	if (isValueSubpageInWhitelist()) {
		activeSectionId.value = 0;
		selectedMobileTab.value = tabList.value.find((data) => data.id === 0)?.value;
		swiperEl.value?.swiperInstance.slideTo(0);
		return;
	}

	const idx = Number(index);
	activeSectionId.value = idx;
	selectedMobileTab.value = tabList.value.find((data) => data.id === idx)?.value;
	if (isEditorMode.value) return;
	swiperEl.value?.swiperInstance.slideTo(idx);
};

const updateUrlWithHash = (value) => {
	const url = new URL(window.location);

	url.hash = encodeURIComponent(value);
	window.location.hash = url.hash;
};

const initContentScrollTriggers = () => {
	const contentWrappers = document.querySelectorAll('.AnchorNavigation-content-wrapper');
	if (!contentWrappers) return;
	
	const HEADER_HEIGHT = document.querySelector('.AppHeader')?.clientHeight || 0;
	const NAV_HEIGHT = document.querySelector('.AnchorNavigation-bar')?.clientHeight || 0;
	const offsetY = NAV_HEIGHT + HEADER_HEIGHT;

	contentWrappers.forEach((node) => {
		const index = node.getAttribute('data-content-id');
		const anchorValue = tabList.value[index].value;
		
		let instance = ScrollTrigger.create({
			trigger: node,
			// start: `top ${offsetY}px`,
			start: `top center`,
			end: `bottom 35%`,
			pinSpacing: false,
			// markers: true,
			onEnter: ({ progress, direction }) => {
				// console.log('onEnter. id = ', index);
				// console.log('---------------');
				if (ScrollTrigger.isScrolling() && ScrollTrigger.isInViewport(node)) {
					updateUrlWithHash(anchorValue);
					activateTab(index);
				}
				
				if (isValueSubpageInWhitelist()) {
					activateTab(index);
					
				}
				
			},
			onEnterBack: ({ progress, direction }) => {
				// console.log('onEnterBack. id = ', index);
				// console.log('---------------');
				if (ScrollTrigger.isScrolling() && ScrollTrigger.isInViewport(node)) {
					updateUrlWithHash(anchorValue);
					activateTab(index);
				}
				
				if (isValueSubpageInWhitelist()) {
					activateTab(index);
					
				}
			},
		});
		
		scrollTriggerContentInstances.push(instance);
	});
};

const { hash } = useRouteHash();

const { pause: pauseHashWatch, resume: resumeHashWatch } = useWatchPausable(hash, async (newValue) => {
	// scroll to particular section, based on the url hash value.
	const index = tabList.value.find((data) => data.value === newValue)?.id;
	handleSlideToSection(index);
});

const debouncedResumeHashWatch = _debounce(resumeHashWatch, 100);


const handleSlideToSection = (index) => {
	const HEADER_HEIGHT = document.querySelector('.AppHeader')?.clientHeight || 0;
	const NAV_HEIGHT = document.querySelector('.AnchorNavigation-bar')?.clientHeight || 0;
	const offsetY = NAV_HEIGHT + HEADER_HEIGHT;
	const contentEl = document.querySelector(`[data-content-id="${index}"]`);
	if (!contentEl) return;
	scrollToElement(contentEl, { topOffset: offsetY });
};

const updateStatusSlidesLongerThanScreen = () => {
	const num = tabList.value.length;
	const screenSize = window.screen.width;
	const offsetW = 60;
	const slideWidth = document.querySelector('.swiper-slide')?.clientWidth + offsetW;
	// console.log('isSlidesLongerThanScreen ==> ', slideWidth * num, screenSize);
	if (slideWidth * num > screenSize) {
		isSlidesLongerThanScreen.value = true;
		return;
	}
	isSlidesLongerThanScreen.value = false;
};


const handleSelection = async (value) => {
	const anchorValue = value;
	const selectedTabIndex = tabList.value.findIndex((data) => data.value === value.toLowerCase());
	await nextTick();
	await handleSlideToSection(selectedTabIndex);
	updateUrlWithHash(anchorValue);
};

const onDropdownOverlayUpdateIsVisible = async (flag) => {
	if (!flag) return;
	await nextTick();
	document.querySelector('html').style.position = 'static';
};


const scrollCallback = () => {
	const anchorTabNavigationEl = document.querySelector('.AnchorNavigation-bar');
	const isScrollUp = document.querySelector('html').classList.contains('scroll-dir-up');
	const widthPercentage = (document.body.scrollTop || document.documentElement.scrollTop) / (document.documentElement.scrollHeight - document.documentElement.clientHeight) * 100;
	progressBarPercentage.value = widthPercentage;

	if (isScrollUp && anchorTabNavigationEl.classList.contains('sticked')) {
		anchorTabNavigationEl.classList.add('is-scrolling-up');
	} else {
		anchorTabNavigationEl.classList.remove('is-scrolling-up');
	}

	// scrollTimerId for run any events after scroll end, 
	// mainly for pause url hash watch while scrolling and resume url hash watch after scroll end.
	pauseHashWatch();
	debouncedResumeHashWatch();
};

const initScrollListener = () => {
	document.removeEventListener('scroll', scrollCallback, { passive: true });
	document.addEventListener('scroll', scrollCallback, { passive: true });
};

let resizeObserverInstance = null;

const initContentResizeOberver = () => {
	resizeObserverInstance?.disconnect();
	resizeObserverInstance = new ResizeObserver(async (entries) => {

		if (ScrollTrigger.getAll().length) {
			killAllScrollTrigger();
			initScrollListener();
			await nextTick();
			// initStickyTab();
			initContentScrollTriggers();
			updateStatusSlidesLongerThanScreen();
		}

		ScrollTrigger.refresh(true);
	});
	resizeObserverInstance.observe(document.querySelector('.AnchorNavigation'));
};


const killAllScrollTrigger = () => {
	// scrollTriggerStickyInstance?.kill();
	scrollTriggerContentInstances?.forEach((st) => st.kill());
	
	// scrollTriggerStickyInstance = null;
	scrollTriggerContentInstances = [];
};

const isLoadOnce = ref(false);

watch(isLoadOnce, () => {
	if (isLoadOnce.value) {
		initContentScrollTriggers();
	}
});

onMounted(() => {
	tabList.value = createTabs();
	/* 
		Hardcode a setTimeout value of 500ms for now.
		The issue is, we don't know how long does it take for page to finish layouting,
		as this is due to we implement lazy loading for component a lot,
		so there are quite a few API calls upon initial page load.
	*/

	


	setTimeout(() => {
		init();

	}, 500);
});


onBeforeUnmount(() => {
	resizeObserverInstance.disconnect();
	document.removeEventListener('scroll', scrollCallback, { passive: true });
	
	killAllScrollTrigger();
});

const siteName = window.siteName;

</script>



<template>
<div
	class="AnchorNavigation relative"
	:class="{
		'has-active-section': selectedMobileTab,
		'>md:hidden': isHideInDesktop,
		'md:hidden': isHideInMobile,
	}"
	:data-use-theme="siteName"
>
	<div class="AnchorNavigation-bar-container">
		<div class="AnchorNavigation-bar bg-white">
			<Dropdown
				v-model="selectedMobileTab"
				:options="tabList"
				:labelText="mobileTextLabel"
				:placeholderText="mobileTextLabel"
				:rootAttrs="{
					class: '>md:hidden',
				}"
				:overlayAttrs="{
					'onUpdate:isVisible': onDropdownOverlayUpdateIsVisible,
				}"
				:aria-label="mobileTextLabel"
				@update:model-value="handleSelection"
			>
				<template #singlelabel="{ value }">
					<div class="value-wrapper relative flex-grow mr-2 leading-none line-clamp-1">
						<span class="text-value relative">{{ value.label }}</span>
					</div>
				</template>

				<template v-if="isMobileViewport" #option="{ option }">
					<span class="line-clamp-2">{{ option.label }}</span>
				</template>
				
			</Dropdown>
			<div class="relative px-8 py-3 md:hidden">
				<VueSwiper
					ref="swiperEl"
					variant="slides-auto-width"
					:dormantMode="isEditorMode"
					class="anchor-navigation-swiper"
					:class="{
						'wrapper-align-center': !isSlidesLongerThanScreen,
						'wrapper-align-left': isSlidesLongerThanScreen,
						'is-editor-mode': isEditorMode,
					}"
					slidesPerView="auto"
					:centeredSlides="false"
					:freeMode="true"
					:navigation="{
						nextEl: swiperBtnNextEl,
						prevEl: swiperBtnPrevEl,
					}"
					:mousewheel="{
						releaseOnEdges: true,
					}"
					:slideToClickedSlide="true"
					:paginationClickable="true"
					:simulateTouch="false"
					:loop="false"
					:spaceBetween="siteName === 'MABA' ? 15 : 0"
				>
					<div
						v-for="(tab, index) in tabList"
						:key="`anchor-navigation-${index}`"
						class="anchor-navigation-wrapper"
					>
						<button
							v-aa="[
								{
									clickName: tab.label,
									clickComponentType: 'Button',
									componentName: 'AnchorNavigation',
									componentID: props.componentIdAA,
								},
								{
									name: tab.label,
									type: 'other',
								},
							]"
							type="button"
							class="anchor-navigation-tab text-base text-primary-black-base hover:no-underline text-center block"
							:class="{
								'is-active': activeSectionId == tab.id,
							}"
							@click="handleSelection(tab.value);"
						>
							<span class="line-clamp-1">{{ tab.label }}</span>
						</button>
					</div>
				</VueSwiper>
				<button
					ref="swiperBtnPrevEl"
					v-button-press
					type="button"
					class="swiper-button-prev"
				>
					<icon-far-chevron-left
						class="cta-icon text-sm"
					/>
				</button>
				<button
					ref="swiperBtnNextEl"
					v-button-press
					type="button"
					class="swiper-button-next"
				>
					<icon-far-chevron-right
						class="cta-icon text-sm"
					/>
				</button>
			</div>
			<div class="progress-bar-group">
				<div
					class="progress-bar"
					:style="{
						width: `${progressBarPercentage}%`,
					}"
				></div>
			</div>
		</div>
	</div>

	<div v-if="createParsys.length">
		<div
			v-for="(section, index) in createParsys"
			:key="`anchoring-navigation-${index}`"
			class="AnchorNavigation-content-wrapper"
			:data-content-id="index"
		>
			<Parsys
				:key="`anchoring-navigation-parsys-key-${index}`"
				:index="index"
				:component="section.component"
				:label="`Section Content ${index}`"
			/>
		</div>
	</div>
</div>

<div v-if="isEditorMode && isHideInDesktop" class="generic-container py-10 text-center">
	<p>Hidden in: <span v-if="isHideInDesktop">Desktop</span> <span v-if="isHideInMobile">, Mobile</span></p>
</div>

</template>



<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.AnchorNavigation {

	.swiper-button-prev,
	.swiper-button-next {
		width: 32px;
		height: 32px;
		line-height: 32px;
		transform: translateY(-50%);
		transition: all ease 200ms;
		margin-top: 0;
		border-radius: 50px;
		
		html[dir="rtl"] & {
			transform: scaleX(-1) translateY(-50%);
		}

		&:hover {
			@apply bg-neutral-grey-base/20;
		}

		&.is-mouse-down {
			@apply bg-neutral-grey-base/50;
		}

		&::after {
			content: none;
			height: 26px;
			width: 13px;
		}
	}

	.swiper-button-prev {
		left: 0;
		html[dir="rtl"] & {
			right: 0;
			left: auto;
		}
	}

	.swiper-button-next {
		right: 0;
		html[dir="rtl"] & {
			left: 0;
			right: auto;
		}
	}
	
	.AnchorNavigation-bar {
		:deep(.Dropdown) {
			border-radius: 0;
			border: 0;
			background-color: var(--neutral-white-base);
			color: var(--neutral-grey-ultradark);
			text-align: center;
	
			.text-value {
				font-weight: 600;
			}
	
			.value-wrapper {
				padding: 0 !important;
				margin: 0.5rem 1.25rem 0.5rem 0;
			}
		}
	
		:deep(.multiselect-main) {
			border: 0;
			background-color: var(--neutral-white-base);
		}
	}
}

.has-active-section {
	.AnchorNavigation-bar {
		:deep(.value-wrapper) {
			.text-label {
				display: none;
			}
	
			.text-value {
				top: auto !important;
			}
		}
	}
}

.AnchorNavigation-bar-container {
	height: 55px;
	
	@media #{var.$query-min-md} {
		height: 77px;
	}
}

.anchor-navigation-tab {
	min-width: 110px;
	height: 48px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: var(--primary-black-base);
	@apply p-4 rounded-xl;

	&:hover {
		background-color: var(--neutral-grey-extralight);
		transition: all ease 200ms;
	}

}

.is-active {
	background-color: var(--primary-blue-extralight);
	color: var(--primary-blue-base);
}

.swiper-button-next.swiper-button-disabled,
.swiper-button-prev.swiper-button-disabled {
	opacity: 0;
}

.sticked {
	position: fixed;
    top: 0;
	left: 0;
	right: 0;
	z-index: 100;
    width: 100%;
	transition: 200ms ease-in-out;

	&.is-scrolling-up {
		transform: translateY(calc(var(--headerHeight) + var(--mobileDlAppNotificationHeight) + var(--headerNotificationHeight)));
	}
}

.anchor-navigation-swiper {
	align-items: center;

	:deep(.swiper-wrapper) {
		width: auto;
	}

	&.swiper-align-center :deep(.swiper-wrapper) {
		justify-content: center;
	}

	&.swiper-align-left :deep(.swiper-wrapper) {
		justify-content: flex-start;
	}

	:deep(.swiper-slide) {
		@media #{var.$query-min-md} {
			max-width: 400px;
		}
	}

	&.is-editor-mode {
		flex-direction: initial;
	}
}

[data-use-theme="MAG"] {
	.anchor-navigation-tab {
		font-weight: 500;
		color: var(--primary-midnightblack-base);
	}
	
	.is-active {
		background-color: var(--primary-midnightgreen-extralight);
		color: var(--primary-midnightgreen-dark);
	}
	
	.has-active-section {
		.AnchorNavigation-bar {
			:deep(.value-wrapper) {
				.text-value {
					color: var(--primary-black-base);
				}
			}
		}
	}
}

[data-use-theme="MHH"] {
	.AnchorNavigation {
		.swiper-button-prev,
		.swiper-button-next {
			fill: var(--primary-mhh-teal-base);
		}
	}
	.anchor-navigation-tab {
		font-weight: 500;
		color: var(--neutral-grey-extradark);

		&:hover {
			background-color: var(--neutral-white-base);
			color: var(--secondary-mhh-teal-dark);
		}
	}
	
	.is-active {
		background-color: var(--neutral-white-base);
		color: var(--primary-mhh-teal-base);
	}

	.has-active-section {
		.AnchorNavigation-bar {
			:deep(.value-wrapper) {
				.text-value {
					color: var(--primary-black-base);
				}
			}
		}
	}
}

// MABA rounded corner
[data-use-theme="MABA"] {
	.anchor-navigation-tab {
		border-radius: 100px;
	}

	.is-active {
		background-color: var(--primary-blue-light);
		color: var(--neutral-white-base);
	}
}

[data-use-theme="amal"] {
	
	.anchor-navigation-tab {
		font-weight: 600;
	}
}

// Firefly
.AnchorNavigation[data-use-theme="firefly"] {
	.anchor-navigation-tab {
		--focus-visible-outline-offset: -2px;
		
		&:hover {
			background-color: transparent;
			color: var(--secondary-firefly-orange-base);
		}
	}

	.is-active {
		background-color: transparent;
		color: var(--secondary-firefly-orange-base);
		font-weight: 500;
	}
}
</style>

